<template>
  <div
    class="video-wrapper"
    ref="videoWrapper"
    :class="positionName"
    :style="{ height: source === 'series' ? 'calc(100% - 56px)' : '100%' }"
  >
    <Loading v-if="item.can_play && state.loading" style="position: absolute" />
    <!-- 核心改造：动态绑定跨端video属性，移除硬编码的muted -->
    <video
      ref="videoEl"
      class="video-js vjs-big-play-centered"
      :poster="item.pic"
      controlsList="nodownload"
      preload="metadata"
      v-bind="videoDynamicAttrs"
    >
      <source :src="item.video" type="video/mp4" />
      <p>您的浏览器不支持 video 标签。</p>
    </video>

    <!-- 原有所有元素：暂停图标、浮层、进度条、选集栏 完全保留 -->
    <img
      src="@/assets/img/icon/play.png"
      class="pause-icon"
      v-if="!isPlaying"
    />
    <div class="float" :class="item.can_play ? '' : 'lock'">
      <template v-if="isLive">
        <div class="living">点击进入直播间</div>
        <ItemDesc
          :is-live="true"
          v-model:item="state.localItem"
          :position="position"
        />
      </template>
      <template v-else>
        <div :style="{ opacity: state.isMove ? 0 : 1 }" class="normal">
          <template v-if="!state.commentVisible && !isScreen">
            <ItemToolbar v-model:item="state.localItem" />
            <ItemDesc v-model:item="state.localItem" :source="source" />
          </template>
        </div>
        <div
          v-if="source === 'home'"
          class="w-full h-[35px] flex justify-between more"
          @click="goDetail(item.drama_id)"
        >
          <PlayCircleStrokeIcon class="mr-[5px] rounded" size="16px" /><span
            class="flex-grow-1"
            >观看完整短剧·全{{ item.total }}集</span
          ><ChevronRightIcon size="20px" />
        </div>
        <div
          v-if="isCurrent"
          class="progress"
          :class="progressClass"
          ref="progressEl"
          @click="null"
          @touchstart="touchstart"
          @touchmove="touchmove"
          @touchend="touchend"
        >
          <div class="time" v-if="state.isMove">
            <span class="currentTime">{{ _duration(state.currentTime) }}</span>
            <span class="duration"> / {{ _duration(state.duration) }}</span>
          </div>
          <template v-if="state.duration > 15 || state.isMove || !isPlaying">
            <div class="bg"></div>
            <div class="progress-line" :style="durationStyle"></div>
          </template>
        </div>
        <template v-if="!item.can_play">
          <div class="video-lock" @click="showBuy">
            <t-icon name="lock-on" size="35" />
            <span class="mt-3">解锁后可看</span>
          </div>
        </template>
      </template>
    </div>
  </div>
  <div
    v-if="source === 'series'"
    class="w-full h-[56px] p-3 flex items-center justify-between text-white"
  >
    <div
      class="w-[300px] h-full flex items-center justify-between bg-[#ffffff33] px-3 rounded"
      v-click="showAnthology"
    >
      <span class="text-base"
        >选集·{{ item.episodes_finish ? "已完结" : "未完结" }}·全{{
          item.total
        }}集</span
      >
      <ChevronUpIcon size="22px" />
    </div>
    <SystemUnlockedIcon
      v-if="!isScreen"
      size="22px"
      v-click="() => (isScreen = !isScreen)"
    />
    <SystemLockedIcon
      v-else
      size="22px"
      v-click="() => (isScreen = !isScreen)"
    />
  </div>
</template>

<script setup lang="ts">
import { _checkImgUrl, _duration, _stopPropagation } from "@/utils";
import Loading from "../Loading.vue";
import ItemToolbar from "./ItemToolbar.vue";
import ItemDesc from "./ItemDesc.vue";
import bus, { EVENT_KEY } from "@/utils/bus";
import { SlideItemPlayStatus } from "@/utils/const_var";
import {
  computed,
  onMounted,
  onUnmounted,
  provide,
  reactive,
  ref,
  nextTick,
} from "vue";
import { _css } from "@/utils/dom";
import { useClick } from "@/utils/hooks/useClick";
import { useCommonStoreHook } from "@/store";
// 核心：引入 Video.js 及样式
import videojs from "video.js";
import "video.js/dist/video-js.css";

const commonStore = useCommonStoreHook();
const vClick = useClick();

defineOptions({
  name: "BaseVideo",
});

const props = defineProps({
  item: {
    type: Object,
    default: () => ({}),
  },
  position: {
    type: Object,
    default: () => ({}),
  },
  isPlay: {
    type: Boolean,
    default: false,
  },
  isMy: {
    type: Boolean,
    default: false,
  },
  isLive: {
    type: Boolean,
    default: false,
  },
  source: {
    type: String,
    default: "",
  },
});

// 提供依赖：逻辑完全不变
provide(
  "isPlaying",
  computed(() => isPlaying)
);
provide(
  "isMuted",
  computed(() => state.isMuted)
);
provide(
  "position",
  computed(() => props.position)
);
provide(
  "item",
  computed(() => props.item)
);

// 核心改造1：设备检测工具函数（区分PC/安卓/X5内核）
const deviceDetect = {
  isPc: () => {
    const userAgent = navigator.userAgent;
    const reg = /Windows|Macintosh|Linux|Ubuntu/;
    return (
      reg.test(userAgent) && !/Android|Adr|iOS|iPhone|iPad|iPod/.test(userAgent)
    );
  },
  isAndroid: () => /Android|Adr/.test(navigator.userAgent),
  isX5: () =>
    /TBS|X5/.test(navigator.userAgent) &&
    /Android|Adr/.test(navigator.userAgent),
  isSpecialX5: () =>
    /Douyin|TikTok|Quark/.test(navigator.userAgent) && deviceDetect.isX5(),
};
const isPc = deviceDetect.isPc();
const isAndroid = deviceDetect.isAndroid();
const isX5 = deviceDetect.isX5();

// 核心：Video.js 相关引用（替换原 plyrRef）
const videoEl = ref<HTMLVideoElement | null>(null); // Video.js 挂载元素
const progressEl = ref<HTMLDivElement | null>(null);
const videoWrapper = ref<HTMLDivElement | null>(null);
let player: videojs.Player | null = null; // Video.js 实例（全局可调用）

// 状态管理：保留原有所有状态，移除 plyr 相关字段
let state = reactive({
  loading: true,
  paused: false,
  isMuted: false, // 静音状态由设备端动态控制
  status: props.isPlay ? SlideItemPlayStatus.Play : SlideItemPlayStatus.Pause,
  duration: 0,
  step: 0,
  currentTime: -1,
  playX: 0,
  start: { x: 0 },
  last: { x: 0, time: 0 },
  height: 0,
  width: 0,
  isMove: false,
  ignoreWaiting: false,
  test: [1, 2],
  localItem: props.item,
  progressBarRect: {
    height: 0,
    width: 0,
  },
  videoScreenHeight: 0,
  commentVisible: false,
  anthologyVisible: false,
  isMounted: false, // 标记组件 DOM 挂载完成
});

// 常量与计算属性：完全保留原有逻辑
const playLock = ref(false);
const isCurrent = ref(true);
const isScreen = ref(false);
const durationStyle = computed(() => ({ width: state.playX + "px" }));
const isPlaying = computed(() => state.status === SlideItemPlayStatus.Play);
const positionName = computed(
  () => "item-" + Object.values(props.position).join("-")
);
const progressClass = computed(() => {
  if (state.isMove) return "move";
  return isPlaying.value ? "" : "stop";
});

// 核心改造2：动态video标签属性（PC/安卓差异化，强制安卓内联）
const videoDynamicAttrs = computed(() => {
  // PC端：保留基础内联配置，静音由业务控制
  if (isPc) {
    return {
      playsinline: true,
      webkitPlaysinline: true,
      muted: state.isMuted,
    };
  }
  // 安卓端：强制内联专属配置（全内核兜底，禁止自动全屏）
  return {
    // 标准内核内联属性
    playsinline: true,
    webkitPlaysinline: true,
    mozPlaysinline: true,
    msPlaysinline: true,
    // X5内核专属内联核心属性（微信/QQ/抖音/夸克必设）
    "x5-video-player-type": "h5-page",
    "x5-video-player-fullscreen": false,
    "x5-video-orientation": "portrait",
    "x5-playsinline": true,
    // 抖音/夸克X5专属内联
    "disable-inline-playback": false,
    "webkit-disable-inline-playback": false,
    // 安卓强制静音（满足内联放行规则）
    muted: true,
    "inline-playback": true,
  };
});

// 核心改造3：Video.js 差异化配置（PC/安卓区分，屏蔽安卓全屏能力）
const videoJsOptions = computed(() => ({
  autoplay: false, // 跨端统一：禁止自动播放（需用户交互）
  muted: isAndroid ? true : state.isMuted, // 安卓强制静音，PC由业务控制
  controls: false, // 关键：禁用 Video.js 默认控件，使用原有自定义 UI
  fluid: true, // 自适应容器宽高（核心配置，适配 100% 宽高）
  preload: "metadata", // 仅预加载元数据（时长、分辨率），优化加载速度
  playbackRates: [0.5, 1, 1.5, 2], // 倍速选项（预留）
  controlBar: {
    fullscreenToggle: isPc, // PC端显示全屏按钮，安卓端隐藏
    volumePanel: false, // 隐藏默认音量控件
    progressControl: false, // 隐藏默认进度条
  },
  userActions: {
    hotkeys: false, // 禁用快捷键，避免与业务交互冲突
  },
  disableContextMenu: true, // 禁用右键菜单
  mobile: {
    forceNativeControls: false,
    suppressNativeFullscreen: isAndroid, // 安卓禁止原生全屏，强制内联
  },
  fullscreen: {
    options: {
      navigationUI: "hide",
      enableInSafari: false,
    },
  },
  // 安卓端核心：允许内联，阻止浏览器全屏
  allowInlinePlayback: isAndroid,
  suppressBrowserFullscreen: isAndroid,
}));

onMounted(async () => {
  state.height = document.body.clientHeight;
  state.width = document.body.clientWidth;
  await nextTick();
  state.isMounted = true;

  // 初始化 Video.js 实例（同步执行，无异步问题）
  if (videoEl.value) {
    player = videojs(videoEl.value, videoJsOptions.value, () => {
      console.log(`${isPc ? "PC端" : "安卓端"} Video.js 实例初始化完成`);
      // 实例就绪后初始化音量
      if (player) player.volume(1);
      // 安卓端兜底：强制锁定静音，确保内联
      if (isAndroid) {
        state.isMuted = true;
        player.muted(true);
        // 禁用所有全屏方法，从底层杜绝全屏
        if (player.requestFullscreen) player.requestFullscreen = () => false;
        if (player.x5EnterFullscreen) player.x5EnterFullscreen = () => false;
      }
    });

    // 绑定 Video.js 核心事件，映射原有状态更新逻辑
    bindVideoJsEvents();

    // 安卓X5内核兜底：JS强制设置内联属性，防止浏览器忽略
    if (isAndroid && isX5 && videoEl.value) {
      const forceInlineAttrs = {
        playsinline: "true",
        "webkit-playsinline": "true",
        "x5-playsinline": "true",
        "x5-video-player-fullscreen": "false",
        muted: "true",
        "disable-inline-playback": "false",
      };
      Object.entries(forceInlineAttrs).forEach(([key, value]) => {
        videoEl.value!.setAttribute(key, value);
      });
    }
  }

  // 原有事件总线：完全保留，仅在调用处增加实例校验
  bus.on(EVENT_KEY.SINGLE_CLICK_BROADCAST, click);
  bus.on(EVENT_KEY.DIALOG_MOVE, onDialogMove);
  bus.on(EVENT_KEY.DIALOG_END, onDialogEnd);
  bus.on(EVENT_KEY.OPEN_COMMENTS, onOpenComments);
  bus.on(EVENT_KEY.CLOSE_COMMENTS, onCloseComments);
  bus.on(EVENT_KEY.OPEN_SUB_TYPE, onOpenSubType);
  bus.on(EVENT_KEY.CLOSE_SUB_TYPE, onCloseSubType);
  bus.on(EVENT_KEY.REMOVE_MUTED, removeMuted);
  bus.on(EVENT_KEY.OPEN_ANTHOLOGY, onOpenAnthology);
  bus.on(EVENT_KEY.CLOSE_ANTHOLOGY, onCloseAnthology);
  bus.on("update:comment:count", updCommentCount);
});

onUnmounted(() => {
  state.isMounted = false;
  // 销毁 Video.js 实例，彻底释放资源（避免内存泄漏）
  if (player) {
    player.off(); // 移除所有事件监听
    player.dispose(); // 销毁实例
    player = null;
  }

  // 原有事件总线解绑：完全保留
  bus.off(EVENT_KEY.SINGLE_CLICK_BROADCAST, click);
  bus.off(EVENT_KEY.DIALOG_MOVE, onDialogMove);
  bus.off(EVENT_KEY.DIALOG_END, onDialogEnd);
  bus.off(EVENT_KEY.OPEN_COMMENTS, onOpenComments);
  bus.off(EVENT_KEY.CLOSE_COMMENTS, onCloseComments);
  bus.off(EVENT_KEY.OPEN_SUB_TYPE, onOpenSubType);
  bus.off(EVENT_KEY.CLOSE_SUB_TYPE, onCloseSubType);
  bus.off(EVENT_KEY.REMOVE_MUTED, removeMuted);
  bus.off(EVENT_KEY.OPEN_ANTHOLOGY, onOpenAnthology);
  bus.off(EVENT_KEY.CLOSE_ANTHOLOGY, onCloseAnthology);
  bus.off("update:comment:count", updCommentCount);
});

// 核心：绑定 Video.js 事件，映射原有状态更新（替代原 plyr 事件）
const bindVideoJsEvents = () => {
  if (!player) return;

  // 元数据加载完成（获取时长、分辨率）
  player.on("loadedmetadata", () => {
    state.duration = player.duration() || 0;
    state.videoScreenHeight =
      player.videoHeight() / (player.videoWidth() / state.width) ||
      state.height;

    // 初始化进度条步长
    if (progressEl.value) {
      state.progressBarRect = progressEl.value.getBoundingClientRect();
      state.step =
        state.progressBarRect.width / Math.floor(state.duration || 1);
    }

    // 自动播放：组件挂载+实例就绪+可播放 三者满足
    if (props.isPlay && state.isMounted && props.item.can_play) {
      play().catch(() => {});
    }
  });

  // 开始播放
  player.on("play", () => {
    state.loading = false;
    state.status = SlideItemPlayStatus.Play;
    // 安卓端兜底：检测是否全屏，非全屏则强制保持内联（禁止自动全屏）
    if (isAndroid) {
      const isFull =
        !!document.fullscreenElement || !!document.webkitFullscreenElement;
      if (isFull) {
        document.exitFullscreen?.();
        document.webkitExitFullscreen?.();
      }
    }
  });

  // 暂停播放
  player.on("pause", () => {
    state.status = SlideItemPlayStatus.Pause;
  });

  // 播放进度更新（核心，同步自定义进度条）
  player.on("timeupdate", () => {
    if (!player) return;
    state.currentTime = Math.ceil(player.currentTime());
    state.playX = (state.currentTime - 1) * state.step;
  });

  // 视频缓冲中（显示加载）
  player.on("waiting", () => {
    if (!state.paused && !state.ignoreWaiting) state.loading = true;
  });

  // 视频可播放（隐藏加载）
  player.on("playing", () => {
    state.loading = false;
  });

  // 播放完成（切下一集）
  player.on("ended", () => {
    bus.emit(EVENT_KEY.NEXT_VIDEO);
  });

  // 加载错误
  player.on("error", () => {
    state.loading = false;
    if (player && player.error()) {
      console.error("Video.js 加载失败:", player.error().message);
    }
  });

  // 数据加载完成（隐藏加载）
  player.on("loadeddata", () => {
    state.loading = false;
  });

  // 核心改造：全屏状态变化监听，安卓端退出全屏不暂停（保持内联）
  player.on("fullscreenchange", () => {
    if (isPc) {
      const isFull =
        !!document.fullscreenElement || !!document.webkitFullscreenElement;
      if (!isFull) state.status = SlideItemPlayStatus.Pause;
    }
    // 安卓端忽略全屏事件，强制保持内联播放
  });
};

// 原有工具方法：完全保留，涉及视频操作增加 player 实例校验
const updCommentCount = (id) => {
  if (props.item.video_id === id) props.item.comments++;
};
const removeMuted = () => {
  // 仅PC端允许取消静音，安卓端强制锁定
  if (!isAndroid) state.isMuted = false;
};
const onOpenSubType = () => {
  state.commentVisible = true;
};
const onCloseSubType = () => {
  state.commentVisible = false;
};
const onDialogMove = ({ tag, e }) => {
  if (state.commentVisible && tag === "comment" && player) {
    _css(player.el(), "transition-duration", `0ms`);
    _css(player.el(), "height", `calc(var(--vh, 1vh) * 30 + ${e}px)`);
  }
};
const onDialogEnd = ({ tag, isClose }) => {
  if (state.commentVisible && tag === "comment" && player) {
    _css(player.el(), "transition-duration", `300ms`);
    if (isClose) {
      state.commentVisible = false;
      _css(player.el(), "height", "100%");
    } else {
      _css(player.el(), "height", "calc(var(--vh, 1vh) * 30)");
    }
  }
};
const onOpenComments = (id) => {
  if (id === props.item.aweme_id && player) {
    _css(player.el(), "transition-duration", `300ms`);
    _css(player.el(), "height", "calc(var(--vh, 1vh) * 30)");
    state.commentVisible = true;
  }
};
const onCloseComments = () => {
  if (state.commentVisible && player) {
    _css(player.el(), "transition-duration", `300ms`);
    _css(player.el(), "height", "100%");
    state.commentVisible = false;
  }
};
const onOpenAnthology = (id) => {
  if (id === props.item.aweme_id) state.anthologyVisible = true;
};
const onCloseAnthology = () => {
  state.anthologyVisible = false;
};
const showAnthology = () => {
  bus.emit(EVENT_KEY.OPEN_ANTHOLOGY, props.item.aweme_id);
};
const showBuy = () => {
  bus.emit(EVENT_KEY.OPEN_BUY, props.item.aweme_id);
};
const goDetail = (id) => {
  bus.emit("goDetail", id);
};

// 核心改造4：播放方法（安卓端强制静音+屏蔽全屏，保留原有逻辑）
const play = async () => {
  // 四重校验：加锁+组件挂载+实例就绪+可播放
  if (playLock.value || !state.isMounted || !player || !props.item.can_play) {
    state.status = SlideItemPlayStatus.Pause;
    console.warn("播放条件不满足：", {
      playLock: playLock.value,
      isMounted: state.isMounted,
      hasPlayer: !!player,
      canPlay: props.item.can_play,
    });
    return;
  }

  playLock.value = true;
  try {
    state.status = SlideItemPlayStatus.Play;
    player.volume(1); // 初始化音量
    // 设备差异化静音：安卓强制静音（确保内联），PC由业务控制
    if (isAndroid) {
      state.isMuted = true;
      player.muted(true);
    } else {
      if (state.isMuted) player.muted(false);
    }
    await player.play(); // Video.js play 方法返回 Promise
    state.loading = false;
  } catch (err) {
    const errorMsg = (err as Error).message || "";
    const silentErrors = [
      "AbortError",
      "play() request was interrupted by a call to pause()",
    ];
    if (!silentErrors.some((msg) => errorMsg.includes(msg))) {
      console.warn(`[视频播放异常] ID: ${props.item.aweme_id}`, errorMsg);
    }
    state.status = SlideItemPlayStatus.Pause;
    state.loading = false;
  } finally {
    playLock.value = false;
  }
};

// 核心：暂停方法（基于 Video.js API）
const pause = () => {
  if (playLock.value || !player) return;
  playLock.value = true;
  try {
    state.status = SlideItemPlayStatus.Pause;
    player.pause(); // 同步暂停，立即生效
  } catch (err) {
    console.warn("暂停视频时出错:", (err as Error).message);
  } finally {
    playLock.value = false;
  }
};

// 点击事件处理：保留原有逻辑，替换为 Video.js API
const click = ({ uniqueId, index, type }) => {
  if (!state.isMounted || !player) return;
  if (props.position.uniqueId === uniqueId && props.position.index === index) {
    if (type === EVENT_KEY.ITEM_TOGGLE) {
      if (props.isLive) {
        pause();
        bus.emit(EVENT_KEY.NAV, {
          path: "/home/live",
          query: { id: props.item.aweme_id },
        });
      } else {
        state.status === SlideItemPlayStatus.Play ? pause() : play();
      }
    }
    if (type === EVENT_KEY.ITEM_STOP) {
      isCurrent.value = false;
      state.ignoreWaiting = true;
      pause();
      player.currentTime(0); // 重置播放进度
      state.ignoreWaiting = false;
    }
    if (type === EVENT_KEY.ITEM_PLAY) {
      if (!props.item.can_play) return;
      isCurrent.value = true;
      state.ignoreWaiting = true;
      player.currentTime(0); // 重置播放进度
      play().finally(() => {
        state.ignoreWaiting = false;
      });
    }
  }
};

// 进度条拖拽：保留原有交互，替换为 Video.js API 设置进度
const touchstart = (e) => {
  _stopPropagation(e);
  state.start.x = e.touches[0].pageX;
  state.last.x = state.playX;
  state.last.time = state.currentTime;
};
const touchmove = (e) => {
  _stopPropagation(e);
  state.isMove = true;
  pause(); // 拖拽时暂停
  let dx = e.touches[0].pageX - state.start.x;
  state.playX = state.last.x + dx;
  state.currentTime = state.last.time + Math.ceil(Math.ceil(dx) / state.step);
  // 边界限制：0 ~ 总时长
  state.currentTime = Math.max(0, Math.min(state.currentTime, state.duration));
};
const touchend = (e) => {
  _stopPropagation(e);
  if (isPlaying.value || !player) return;
  setTimeout(() => (state.isMove = false), 1000);
  // 拖拽结束后设置进度并恢复播放
  if (state.isMounted && props.item.can_play) {
    player.currentTime(state.currentTime); // Video.js 设置播放进度
    play().catch(() => {});
  }
};
</script>

<style scoped lang="scss">
// 原有样式 + Video.js 容器适配 + 安卓内联兜底样式
.video-wrapper {
  position: relative;
  font-size: 14px;
  width: 100%;
  height: 100%;
  text-align: center;
  z-index: 1;

  // Video.js 容器适配：强制占满父元素，隐藏默认控件残留
  :deep(.video-js) {
    width: 100% !important;
    height: 100% !important;
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    z-index: 1; // 播放器在底层，浮层在上方
    border: none !important;
    background: transparent !important;
    transition: height 0.3s !important;
  }
  :deep(.vjs-tech) {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
    object-position: center !important;
  }
  // 隐藏 Video.js 大播放按钮（使用自定义暂停/播放图标）
  :deep(.vjs-big-play-button) {
    display: none !important;
  }
  // 兜底：隐藏所有全屏相关控件（安卓端彻底屏蔽）
  :deep(.vjs-fullscreen-toggle) {
    display: none !important;
  }

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
    margin: 0;
    padding: 0;
    border: none;
    transition: height 0.3s;
  }

  .float {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 100%;
    z-index: 10; // 浮层高于播放器

    .normal {
      position: absolute;
      bottom: 0;
      width: 100%;
      transition: all 0.3s;
    }

    .more {
      z-index: 10;
      position: absolute;
      bottom: 0px;
      background: #00000066;
      color: #fff;
      text-align: left;
      padding: 5px 10px;
      align-items: center;
    }

    .progress {
      z-index: 11; // 进度条高于浮层
      $w: 100%;
      position: absolute;
      bottom: -4px;
      height: 10px;
      width: $w;
      display: flex;
      align-items: center;

      .time {
        position: absolute;
        z-index: 9;
        font-size: 24px;
        bottom: 50px;
        left: 0;
        right: 0;
        color: white;
        text-align: center;

        .duration {
          color: darkgray;
        }
      }

      $radius: 10px;
      $h: 2px;
      $tr: height 0.1s;

      .bg {
        transition: $tr;
        position: absolute;
        width: 100%;
        height: $h;
        background: #4f4f4f;
      }

      $p: 50px;

      .progress-line {
        transition: $tr;
        height: calc($h + 0.5px);
        width: $p;
        background: #777777;
        z-index: 1;
      }
    }

    & .move {
      $h: 10px;
      opacity: 1;
      .bg {
        height: $h;
      }
      .progress-line {
        height: $h;
        background: white;
      }
    }

    & .stop {
      $h: 4px;
      opacity: 1;
      .bg {
        height: $h;
      }
      .progress-line {
        height: $h;
        background: white;
      }
    }
  }

  .float.lock {
    background: #00000066;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
  }
}

// 原有样式完全保留，无修改
.living {
  position: absolute;
  left: 50%;
  font-size: 18px;
  border-radius: 50px;
  border: 1px solid #e0e0e0;
  padding: 15px 20px;
  line-height: 1;
  color: white;
  top: 70%;
  transform: translate(-50%, -50%);
  z-index: 10;
}
.video-lock {
  position: absolute;
  left: 50%;
  font-size: 18px;
  border-radius: 50px;
  padding: 15px 20px;
  line-height: 1;
  color: white;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 10;
  cursor: pointer;
}
.pause-icon {
  width: 4.75rem;
  height: 4.75rem;
  opacity: 0.3;
  position: absolute;
  margin: auto;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  color: white;
  animation: pause-animation 0.1s linear forwards;
  z-index: 10;
  pointer-events: none;

  @keyframes pause-animation {
    0% {
      opacity: 0;
      transform: scale(2);
    }
    100% {
      opacity: 0.3;
      transform: scale(1.2);
    }
  }
}
</style>